import { IncomeHourlyResponse } from "@/api/kefc";
import { CityKey } from "@/features/services";

/**
 * «Спрос сейчас» — из заказов kef-compare (`/api/income_hourly`).
 *
 * Спрос меряем заказами, а не курьеро-часами: заказы доезжают за минуты и есть
 * даже за текущий час, часы онлайна дозревают двое суток. Для каждого тарифа
 * строим типовой профиль суток — среднее число заказов по часу суток за окно —
 * и смотрим, где в этом профиле стоит час курсора. Ступень — по квантилю
 * профиля, как в прототипе (`demandLvl`): ниже 35% часов суток — «низкий»,
 * выше 90% — «пик». Так уровень не зависит от масштаба города.
 */
export interface DemandProfile {
  /** Среднее заказов по часу суток (0–23, время города). */
  typical: number[];
  /** Фактические заказы по эпохе-часу. */
  actual: Map<number, number>;
}

export interface Demand {
  profiles: Map<number, DemandProfile>;
  /**
   * Последний ПОЛНЫЙ час данных: самый свежий час набирается прямо сейчас, и
   * заказов в нём всегда меньше — по нему уровень был бы занижен.
   */
  lastCompleteEpoch: number;
}

export function buildDemand(
  payload: IncomeHourlyResponse,
  city: CityKey,
  tz: number
): Demand {
  const ci = payload.cities.indexOf(city);
  const f = payload.fields;
  const iCity = f.indexOf("city");
  const iTariff = f.indexOf("tariff");
  const iH = f.indexOf("h");
  const iOrders = f.indexOf("orders");
  const profiles = new Map<number, DemandProfile>();
  let maxEpoch = -Infinity;
  if (ci < 0 || iCity < 0 || iTariff < 0 || iH < 0 || iOrders < 0) {
    return { profiles, lastCompleteEpoch: maxEpoch };
  }

  const sums = new Map<number, { sum: number[]; cnt: number[] }>();
  payload.rows.forEach((r) => {
    if (r[iCity] !== ci) return;
    const tariff = r[iTariff] as number;
    const epoch = payload.h0 + (r[iH] as number);
    const orders = (r[iOrders] as number | null) ?? 0;
    const localHour = (((epoch + tz) % 24) + 24) % 24;
    maxEpoch = Math.max(maxEpoch, epoch);
    let p = profiles.get(tariff);
    if (!p) {
      p = { typical: Array(24).fill(0), actual: new Map() };
      profiles.set(tariff, p);
      sums.set(tariff, { sum: Array(24).fill(0), cnt: Array(24).fill(0) });
    }
    p.actual.set(epoch, orders);
    const s = sums.get(tariff) as { sum: number[]; cnt: number[] };
    s.sum[localHour] += orders;
    s.cnt[localHour] += 1;
  });
  sums.forEach((s, tariff) => {
    const p = profiles.get(tariff) as DemandProfile;
    p.typical = s.sum.map((v, h) => (s.cnt[h] ? v / s.cnt[h] : 0));
  });
  return { profiles, lastCompleteEpoch: maxEpoch - 1 };
}

/**
 * Уровень 0–3 для часа `epochHour` (час города — `localHour`).
 *
 * Прошедшие полные часы — по факту, остальные (идущий и будущие) — по типовому
 * профилю. Часы устройства здесь не участвуют вовсе: «сейчас» задаётся данными,
 * иначе у человека в другом поясе или со сбитыми часами уровень уезжал бы.
 */
export function demandLevel(
  demand: Demand | undefined,
  tariffCode: number,
  epochHour: number,
  localHour: number
): number | null {
  const p = demand?.profiles.get(tariffCode);
  if (!p) return null;
  const curve = p.typical;
  if (!curve.some((v) => v > 0)) return null;

  const actual = p.actual.get(epochHour);
  const v =
    actual != null && epochHour <= (demand as Demand).lastCompleteEpoch
      ? actual
      : curve[localHour];

  const pct = curve.filter((x) => x <= v).length / curve.length;
  if (pct < 0.35) return 0;
  if (pct < 0.65) return 1;
  if (pct < 0.9) return 2;
  return 3;
}

export const DEMAND_COLORS = [
  "var(--dem-1)",
  "var(--dem-2)",
  "var(--dem-3)",
  "var(--dem-4)",
];
